/* eslint-disable  */
import React, { useState } from "react";
import { useFormikContext, FieldArray, FieldProps } from "formik";
import Button from "components/base/Button";
import {
  TextField,
  IconButton,
  Stack,
  styled,
  Divider,
  Box,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import { useMutation } from "@apollo/client";
import { GET_CAREPLAN_TEMPLATE } from "graphql/TemplateQueries";
import {
  CREATE_SITE_TEMPLATE,
  UPDATE_SITE_TEMPLATE,
  DELETE_SITE_TEMPLATE,
  UPDATE_SITE_TEMPLATE_NAME,
  MOVE_SITE_TEMPLATE_TO_PRESCRIPTION,
} from "graphql/SiteQueries";
import {
  ADD_SITE_GROUP_TEMPLATE,
  DELETE_SITE_GROUP_TEMPLATE,
} from "graphql/SiteGroupQueries";

import TreatmentSitesTable from "./TreatmentSitesTable";
import DebouncedButton from "components/base/DebouncedButton";
import PrescriptionSites from "./PrescriptionSites";
import { CREATE_PRESCRIPTION } from "graphql/PrescriptionQueries";

type TreatmentSiteType = {
  site: string;
  siteGroupSites: string;
};
interface Props extends FieldProps<any, TreatmentSiteType> {
  label: string;
  buttonLabel: string;
  options: any[];
  templateId: any;
  template: any;
  isActive: boolean;
}

const TreatmentSites = ({
  field,
  label,
  options,
  templateId,
  template,
  form,
  isActive,
}: Props) => {
  const theme = useTheme();
  const [createSiteTemplate] = useMutation(CREATE_SITE_TEMPLATE, {
    refetchQueries: [
      { query: GET_CAREPLAN_TEMPLATE, variables: { templateId } },
    ],
    awaitRefetchQueries: true,
  });

  const [updateSiteTemplate] = useMutation(UPDATE_SITE_TEMPLATE, {
    refetchQueries: [
      { query: GET_CAREPLAN_TEMPLATE, variables: { templateId } },
    ],
    awaitRefetchQueries: true,
  });

  const [deleteSiteTemplate] = useMutation(DELETE_SITE_TEMPLATE, {
    refetchQueries: [
      { query: GET_CAREPLAN_TEMPLATE, variables: { templateId } },
    ],
    awaitRefetchQueries: true,
  });
  const [updateSiteTemplateName] = useMutation(UPDATE_SITE_TEMPLATE_NAME);
  const [createPrescription] = useMutation(CREATE_PRESCRIPTION, {
    refetchQueries: [
      { query: GET_CAREPLAN_TEMPLATE, variables: { templateId } },
    ],
  });
  const [moveSiteTemplateToPrescription] = useMutation(
    MOVE_SITE_TEMPLATE_TO_PRESCRIPTION,
    {
      refetchQueries: [
        { query: GET_CAREPLAN_TEMPLATE, variables: { templateId } },
      ],
    },
  );
  const { errors } = form;
  const { name } = field;

  const prescriptions = template.prescriptionTemplate;
  const prescriptionOptions = prescriptions.map(
    (prescription: any, index: number) => ({
      label: `Prescription Group ${index + 1}`,
      value: prescription.id,
    }),
  );

  return (
    <>
      <h3>{label}</h3>
      <FieldArray name={name}>
        {({ push, remove }) => (
          <>
            <FieldArray
              name="prescriptions"
              render={() => (
                <>
                  {prescriptions.map(
                    (prescription: any, prescriptionIndex: number) => {
                      const treatmentSiteOptions =
                        prescription.siteGroupTemplate
                          .filter(
                            (siteGroup: any) =>
                              siteGroup.siteTemplate.length > 0,
                          )
                          .map((item: any, index: number) => {
                            return {
                              label: `Sim ${index + 1}`,
                              value: item.id,
                            };
                          });
                      const handleDeleteSiteTemplate = (
                        rowIndex: number,
                        siteId: string,
                      ) => {
                        remove(rowIndex);
                        deleteSiteTemplate({ variables: { siteId } });
                      };
                      const sites = prescription.siteGroupTemplate
                        .map((siteGroup: any) =>
                          siteGroup.siteTemplate.map((site: any) => {
                            return {
                              ...site,
                              siteGroupId: siteGroup.id,
                              prescriptionId: prescription.id,
                            };
                          }),
                        )
                        .flat();
                      return (
                        <PrescriptionSites
                          key={prescription.id}
                          templateId={templateId}
                          index={prescriptionIndex}
                          prescriptionId={prescription.id}
                          isActive={isActive}
                        >
                          <div>
                            <div>
                              <div
                                key={prescription.id}
                                style={{ padding: "0px 16px" }}
                              >
                                <TreatmentSitesTable
                                  options={treatmentSiteOptions}
                                  createSiteTemplate={(siteName: string) => {
                                    createSiteTemplate({
                                      variables: {
                                        prescriptionId: prescription.id,
                                        siteGroupId:
                                          prescription.siteGroupTemplate
                                            .length > 0
                                            ? prescription.siteGroupTemplate[0]
                                                .id
                                            : null,
                                        siteName,
                                      },
                                    });
                                  }}
                                  handleDelete={handleDeleteSiteTemplate}
                                  handleSimulationGroupChange={(
                                    siteId: string,
                                    siteGroupId: string,
                                  ) => {
                                    updateSiteTemplate({
                                      variables: {
                                        siteId,
                                        siteGroupId,
                                      },
                                    });
                                  }}
                                  handlePrescriptionChange={(
                                    siteId: string,
                                    prescriptionId: string,
                                  ) => {
                                    moveSiteTemplateToPrescription({
                                      variables: {
                                        siteId: siteId,
                                        prescriptionId: prescriptionId,
                                      },
                                    });
                                  }}
                                  siteTemplate={sites}
                                  siteGroupCount={
                                    prescription.siteGroupTemplate?.length
                                  }
                                  isActive={isActive}
                                  index={prescriptionIndex}
                                  updateSiteName={updateSiteTemplateName}
                                  siteOptions={options}
                                  prescriptionOptions={prescriptionOptions}
                                />
                              </div>
                              {sites.length === 0 && errors.site && (
                                <Stack color="error">{errors.site}</Stack>
                              )}
                            </div>
                          </div>
                        </PrescriptionSites>
                      );
                    },
                  )}
                </>
              )}
            />
            <Box sx={{ padding: "8px 0px" }}>
              <DebouncedButton
                Component={Button}
                id="addPrescription"
                disabled={!isActive}
                delay={5000}
                onClick={() => {
                  createPrescription({
                    variables: {
                      templateId,
                      name: `Prescription ${prescriptions.length + 1}`,
                    },
                  });
                }}
                style={{
                  width: "280px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                }}
              >
                ADD PRESCRIPTION GROUP
              </DebouncedButton>
            </Box>

            {errors.siteGroupSites && (
              <Stack color="error">{errors.siteGroupSites}</Stack>
            )}
          </>
        )}
      </FieldArray>
    </>
  );
};

export default TreatmentSites;
